import React, { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { RoleSummaryStore, RoleSummary } from 'src/stores/roles-store';
import { Selector } from 'src/common/types';
import { generateSelectorOdataFilter, getRoleDisplayName } from 'src/common/utils';
import { ImmutableObject } from '@hookstate/core';

const RoleSelect: FC<
  {
    onChange?: (value: { value: string; label: string; selector: Selector } | null) => void;
    exclude?: string[];
    excludeNames?: string[];
    filterNames?: string[];
    value?: { value: string; label: string; selector?: Selector };
    organizationId?: string;
    minSelector?: Selector;
    maxSelector?: Selector;
    editRoles?: boolean;
    id?: string;
    allowClear?: boolean;
    disabled?: boolean;
  } & WithTranslation
> = ({
  t,
  onChange,
  exclude,
  excludeNames,
  filterNames,
  value,
  organizationId,
  minSelector = Selector.Drillhole,
  maxSelector = Selector.Admin,
  editRoles = false,
  allowClear = true,
  disabled = false,
}) => {
  const roleStore = container.get(RoleSummaryStore);
  const rolesState = roleStore.state;
  const [currentValue, setValue] = useState<{ value: string; label: string } | undefined>(value);

  const load = (search: string | undefined = undefined) => {
    let filter = [generateSelectorOdataFilter(minSelector, maxSelector)];

    if (organizationId != null) filter.push(`OrganizationId eq ${organizationId}`);

    if ((exclude || []).length !== 0) filter = [...filter, `id ne ${exclude?.join(' and id ne ')}`];

    roleStore.load({
      searchQuery: search,
      orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
      skip: 0,
      take: 1000000,
      filter,
    } as Query);
  };

  useEffect(() => {
    if (editRoles) {
      load('');
      return;
    }
    setValue(undefined);
    load('');
    if (currentValue) {
      onChange && onChange(null);
    }
  }, [organizationId, minSelector, maxSelector]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    load(value);
  }, 800);

  const onSelect = (nextValue: { value: string; label: string }, options: any) => {
    setValue(nextValue);
    onChange && onChange({ value: options.value, label: options.label, selector: options.selector });
  };

  return (
    <>
      <Select
        onClear={() => onChange && onChange(null)}
        className="role-select"
        optionLabelProp="label"
        loading={!!rolesState.isBusy.get()}
        labelInValue
        placeholder={t('SelectRole')}
        onChange={onSelect}
        style={{ width: '100%', height: '100%' }}
        allowClear={allowClear}
        disabled={!organizationId || disabled}
        showSearch
        value={currentValue}
        notFoundContent={rolesState.isBusy.get() ? <Spin size="small" /> : undefined}
        filterOption={false}
        showArrow={false}
        onSearch={onSearch}
        onDropdownVisibleChange={(visible) => {
          if (!visible) load();
        }}
      >
        {filterNames &&
          (rolesState.items.value || [])
            .filter((x) => !(excludeNames || []).any((y) => y === x.name))
            .filter((x) => filterNames.any((y) => y === x.name))
            .map((o: ImmutableObject<RoleSummary>) => (
              <Select.Option key={o.id} value={o.id} selector={o.selector} label={o.name}>
                {getRoleDisplayName(o.name)}
              </Select.Option>
            ))}
        {!filterNames &&
          (rolesState.items.value || [])
            .filter((x) => !(excludeNames || []).any((y) => y === x.name))
            .map((o: ImmutableObject<RoleSummary>) => (
              <Select.Option key={o.id} value={o.id} selector={o.selector} label={o.name}>
                {getRoleDisplayName(o.name)}
              </Select.Option>
            ))}
      </Select>
    </>
  );
};

export default withTranslation()(RoleSelect);
